import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { Logo } from "./Logo";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { EnquiryDialog } from "./EnquiryDialog";

const LINKS = [
  { to: "/", label: "Home", id: "home", end: true },
  { to: "/packages", label: "Tour Packages", id: "packages" },
  { to: "/ferry-booking", label: "Ferry Booking", id: "ferry" },
  { to: "/hotel-booking", label: "Hotel Booking", id: "hotel" },
  { to: "/reviews", label: "Reviews", id: "reviews" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const NavItem = ({ l, prefix, onNavigate, children }) => (
  <NavLink to={l.to} end={l.end} onClick={onNavigate} className={linkCls} data-testid={`${prefix}-${l.id}`}>{children}</NavLink>
);

const linkCls = ({ isActive }) =>
  `text-sm font-semibold transition-colors duration-200 ${isActive ? "text-ocean" : "text-slate-700 hover:text-ocean"}`;

export const Navbar = () => {
  const { settings } = useSiteData();
  const [open, setOpen] = useState(false);
  const wa = waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to know more about your Andaman packages.");

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-sky-100 shadow-sm">
      <div className="container-x h-[72px] flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => <NavItem key={l.id} l={l} prefix="nav-link">{l.label}</NavItem>)}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <a href={wa} target="_blank" rel="noreferrer" data-testid="nav-whatsapp-button" aria-label="Chat on WhatsApp" title="Chat on WhatsApp"
            className="h-10 w-10 grid place-items-center rounded-full border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors">
            <WhatsAppIcon className="h-4 w-4" />
          </a>
          <EnquiryDialog
            trigger={<Button data-testid="nav-enquire-button" className="rounded-full h-10 px-5 bg-ocean hover:bg-navy">Get Free Quote</Button>}
          />
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="xl:hidden h-10 w-10 shrink-0 grid place-items-center rounded-full border border-slate-200" data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="h-5 w-5 text-navy" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-white">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-6"><Logo /></div>
            <nav className="mt-10 flex flex-col gap-5">
              {LINKS.map((l) => <NavItem key={l.id} l={l} prefix="mobile-nav-link" onNavigate={() => setOpen(false)}><span className="text-lg">{l.label}</span></NavItem>)}
            </nav>
            <div className="mt-10 flex flex-col gap-3">
              <a href={wa} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-emerald-600 text-white font-semibold">
                <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
              </a>
              <EnquiryDialog
                trigger={<Button data-testid="mobile-enquire-button" className="rounded-full h-11 bg-ocean hover:bg-navy">Get Free Quote</Button>}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
